import { fetchAllPages } from '@core/api/pagination';
import { getFlags, getIsFeatureEnabled } from '@core/flagsmith';
import { boundingBoxToGeometry } from '@core/logic/helper/geometry-converter';
import { photoApiV1 as photoApi, photoClientApiV1 as photoClientApi } from '@deepup/apis';
import { iphotoContentClassificationClientApiV1 as iphotoContentClassificationClientApi } from '@deepup/internal-apis';
import { BoundingBox } from '@viewer3D/types';
import { getTransport } from '../transport';
import { getCurrentUser } from '../user';

// This should be reduced to max 100 as soon as daterange filtering is implemented in photod
export const DEFAULT_PAGE_SIZE = 5000;

export const getPhotoList = async ({
  projectId,
  viewport,
}: {
  projectId: string;
  viewport?: BoundingBox;
}) => {
  const transport = getTransport();
  const flags = getFlags();
  const photoClient = new photoClientApi.PhotoServiceClient(transport);
  const photoClassificationClient =
    new iphotoContentClassificationClientApi.InternalPhotoContentClassificationServiceClient(
      transport,
    );

  const { data: allPhotos } = await fetchAllPages({
    fetchFunction: async ({ options }) =>
      await photoClient.listPhotos({
        projectId,
        viewport: viewport ? boundingBoxToGeometry(viewport) : undefined,
        options,
      }).response,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const user = getCurrentUser();
  const photoClassificationsEnabled = getIsFeatureEnabled(
    flags['photo_classifications_enabled'],
    user,
  );

  if (!photoClassificationsEnabled) {
    return { data: allPhotos };
  }

  const photoIds = allPhotos.map((p) => p.id);

  const { data: photoClassifications } = await fetchAllPages({
    fetchFunction: ({ options }) =>
      photoClassificationClient.listPhotoContentClassifications({
        projectId,
        photoId: '',
        objectName: '',
        minConfidenceScore: 0,
        workerVersion: '',
        photoIds,
        options,
      }).response,
  });

  const photosWithClassification = allPhotos.map((photo) => {
    const classification = photoClassifications.find(
      (classification) => classification.photoId === photo.id,
    );
    return { ...photo, classification };
  });

  return { data: photosWithClassification };
};

export const getPhoto = async ({ photoId }: { photoId: string }) => {
  const transport = getTransport();
  const photoClient = new photoClientApi.PhotoServiceClient(transport);

  const photo = await photoClient.getPhoto({
    id: photoId,
    version: BigInt(0),
  }).response;

  return { data: photo };
};

export const getSignedPhoto = async ({ photoId }: { photoId: string }) => {
  const transport = getTransport();
  const photoClient = new photoClientApi.PhotoServiceClient(transport);

  const url = await photoClient.getPhotoUrl({
    id: photoId,
    version: BigInt(0),
  }).response;

  return { data: url };
};

export const getPhotoCount = async ({
  projectId,
  recordedAfter,
  options,
}: photoApi.ListPhotosRequest) => {
  const transport = getTransport();

  const client = new photoClientApi.PhotoServiceClient(transport);
  const response: photoApi.ListPhotosResponse = await client.listPhotos({
    projectId,
    recordedAfter,
    options: {
      pageSize: options?.pageSize ?? 1,
      page: options?.page ?? BigInt(0),
      contextId: options?.contextId ?? '',
      withPaginationInfo: options?.withPaginationInfo ?? true,
    },
  }).response;

  return { data: response };
};
